var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { isInsideIFrame } from './utils/iframe';
import { NodeSmartLinkProvider } from './lib/NodeSmartLinkProvider';
import { createStorage } from './utils/storage';
import { IFrameCommunicator } from './lib/IFrameCommunicator';
import { IFrameMessageType, } from './lib/IFrameCommunicatorTypes';
import { QueryParamPresenceWatcher } from './lib/QueryParamPresenceWatcher';
import { defineAllRequiredWebComponents } from './web-components/components';
import { ConfigurationManager } from './lib/ConfigurationManager';
import { InvalidEnvironmentError, NotInitializedError } from './utils/errors';
import { Logger, LogLevel } from './lib/Logger';
import { reload } from './utils/reload';
import { EventManager } from './lib/EventManager';
export var KontentSmartLinkEvent;
(function (KontentSmartLinkEvent) {
    KontentSmartLinkEvent["Refresh"] = "refresh";
    KontentSmartLinkEvent["Update"] = "update";
})(KontentSmartLinkEvent || (KontentSmartLinkEvent = {}));
class KontentSmartLinkSDK {
    constructor(configuration) {
        this.initialize = () => __awaiter(this, void 0, void 0, function* () {
            yield defineAllRequiredWebComponents();
            const level = this.configurationManager.debug ? LogLevel.Debug : LogLevel.Info;
            Logger.setLogLevel(level);
            if (this.configurationManager.queryParam) {
                this.queryParamPresenceWatcher.watch(this.configurationManager.queryParam, this.nodeSmartLinkProvider.toggle);
            }
            else {
                this.nodeSmartLinkProvider.enable();
            }
            if (isInsideIFrame()) {
                this.initializeIFrameCommunication();
            }
        });
        this.destroy = () => {
            this.events.removeAllListeners();
            this.queryParamPresenceWatcher.unwatchAll();
            this.iframeCommunicator.destroy();
            this.nodeSmartLinkProvider.destroy();
        };
        this.updateConfiguration = (configuration) => {
            if (typeof configuration.queryParam !== 'undefined') {
                if (!configuration.queryParam) {
                    this.nodeSmartLinkProvider.enable();
                }
                else if (configuration.queryParam !== this.configurationManager.queryParam) {
                    this.queryParamPresenceWatcher.unwatchAll();
                    this.queryParamPresenceWatcher.watch(configuration.queryParam, this.nodeSmartLinkProvider.toggle);
                }
            }
            if (typeof configuration.debug !== 'undefined') {
                const level = configuration.debug ? LogLevel.Debug : LogLevel.Info;
                Logger.setLogLevel(level);
            }
            this.configurationManager.update(configuration);
        };
        this.on = (event, handler) => {
            this.events.on(event, handler);
        };
        this.off = (event, handler) => {
            this.events.off(event, handler);
        };
        this.initializeIFrameCommunication = () => {
            var _a, _b;
            this.iframeCommunicator.initialize();
            const storage = KontentSmartLinkSDK.getSettingsStorage();
            const settings = storage.get();
            const enabled = settings !== null ? settings === null || settings === void 0 ? void 0 : settings.enabled : true;
            const messageData = {
                enabled,
                languageCodename: (_a = this.configurationManager.defaultLanguageCodename) !== null && _a !== void 0 ? _a : null,
                projectId: (_b = this.configurationManager.defaultProjectId) !== null && _b !== void 0 ? _b : null,
                supportedFeatures: {
                    previewIFrameCurrentUrlHandler: true,
                    refreshHandler: true,
                    updateHandler: true,
                },
            };
            this.iframeCommunicator.sendMessageWithResponse(IFrameMessageType.Initialized, messageData, () => {
                this.configurationManager.update({ isInsideWebSpotlight: true });
                this.queryParamPresenceWatcher.unwatchAll();
                this.nodeSmartLinkProvider.disable();
                if (enabled) {
                    this.nodeSmartLinkProvider.enable();
                }
                this.iframeCommunicator.addMessageListener(IFrameMessageType.Status, this.handleStatusMessage);
                this.iframeCommunicator.addMessageListener(IFrameMessageType.RefreshPreview, this.handleRefreshMessage);
                this.iframeCommunicator.addMessageListener(IFrameMessageType.UpdatePreview, this.handleUpdateMessage);
                this.iframeCommunicator.addMessageListener(IFrameMessageType.PreviewIFrameCurrentUrl, this.handlePreviewIFrameCurrentUrlRequestMessage);
            });
        };
        this.handleStatusMessage = (data) => {
            if (!data || !this.nodeSmartLinkProvider)
                return;
            this.nodeSmartLinkProvider.toggle(data.enabled);
            KontentSmartLinkSDK.getSettingsStorage().set({
                enabled: data.enabled,
            });
        };
        this.handleRefreshMessage = (data, metadata) => {
            const isCustomRefreshHandlerImplemented = this.events.hasEventListener(KontentSmartLinkEvent.Refresh);
            if (isCustomRefreshHandlerImplemented) {
                this.events.emit(KontentSmartLinkEvent.Refresh, data, metadata, reload);
            }
            else {
                reload();
            }
        };
        this.handleUpdateMessage = (data) => {
            this.events.emit(KontentSmartLinkEvent.Update, data, undefined, undefined);
        };
        this.handlePreviewIFrameCurrentUrlRequestMessage = () => {
            const messageData = {
                previewUrl: window.location.href,
            };
            this.iframeCommunicator.sendMessage(IFrameMessageType.PreviewIFrameCurrentUrlResponse, messageData);
        };
        this.configurationManager = ConfigurationManager.getInstance();
        this.configurationManager.update(configuration);
        this.events = new EventManager();
        this.queryParamPresenceWatcher = new QueryParamPresenceWatcher();
        this.iframeCommunicator = new IFrameCommunicator();
        this.nodeSmartLinkProvider = new NodeSmartLinkProvider(this.iframeCommunicator);
        this.initialize();
    }
    static getSettingsStorage() {
        return createStorage('kontent-smart-link:iframe-settings');
    }
}
class KontentSmartLink {
    constructor() {
        this.sdk = null;
        this.destroy = () => {
            var _a;
            (_a = this.sdk) === null || _a === void 0 ? void 0 : _a.destroy();
            this.sdk = null;
        };
        this.setConfiguration = (configuration) => {
            if (!this.sdk) {
                throw NotInitializedError('KontentSmartLink is not initialized or has already been destroyed.');
            }
            else {
                this.sdk.updateConfiguration(configuration);
            }
        };
        this.on = (event, handler) => {
            if (!this.sdk) {
                throw NotInitializedError('KontentSmartLink is not initialized or has already been destroyed.');
            }
            else {
                this.sdk.on(event, handler);
            }
        };
        this.off = (event, handler) => {
            if (!this.sdk) {
                throw NotInitializedError('KontentSmartLink is not initialized or has already been destroyed.');
            }
            else {
                this.sdk.off(event, handler);
            }
        };
    }
    static initializeOnLoad(configuration) {
        if (typeof window === 'undefined') {
            throw InvalidEnvironmentError('KontentSmartLink can only be initialized in a browser environment.');
        }
        return new Promise((resolve) => {
            window.addEventListener('load', () => {
                resolve(KontentSmartLink.initialize(configuration));
            });
        });
    }
    static initialize(configuration) {
        if (!KontentSmartLink.instance) {
            KontentSmartLink.instance = new KontentSmartLink();
        }
        if (!KontentSmartLink.instance.sdk) {
            KontentSmartLink.instance.sdk = new KontentSmartLinkSDK(configuration);
        }
        return KontentSmartLink.instance;
    }
}
export default KontentSmartLink;
//# sourceMappingURL=sdk.js.map